/* eslint-disable react-refresh/only-export-components */
/**
 * MicroConfirmation
 *
 * A brief, auto-dismissing confirmation that explains the impact of an action.
 * Builds mental models without documentation by showing WHY an action mattered.
 *
 * Design principles:
 * - One sentence explaining impact
 * - Auto-dismisses (no user action required)
 * - Non-blocking (doesn't prevent continued work)
 * - Contextual (appears near where action happened)
 *
 * Usage:
 * showMicroConfirmation({
 *   message: "Saved. This note now appears in the donor's timeline.",
 *   duration: 4000,
 * });
 */

import { Check, X } from "lucide-react";
import React, { createContext, useContext, useState, useCallback } from "react";

// ============================================================================
// Types
// ============================================================================

export interface MicroConfirmationConfig {
  /** The confirmation message (one sentence, explains impact) */
  message: string;
  /** How long to show before auto-dismiss (ms, default 4000) */
  duration?: number;
  /** Optional icon to show (default: checkmark) */
  icon?: React.ReactNode;
  /** Variant for styling */
  variant?: "success" | "info" | "neutral";
  /**
   * Optional key for first-use tracking. If provided, this confirmation
   * will only show once per key (per browser). Useful for onboarding moments.
   */
  firstUseKey?: string;
}

interface MicroConfirmationState extends MicroConfirmationConfig {
  id: string;
  visible: boolean;
}

interface MicroConfirmationContextValue {
  show: (config: MicroConfirmationConfig) => void;
  /** Check if a first-use confirmation has already been shown */
  hasShown: (key: string) => boolean;
  /** Manually mark a key as shown (useful for conditional logic) */
  markAsShown: (key: string) => void;
}

// ============================================================================
// Context
// ============================================================================

const MicroConfirmationContext =
  createContext<MicroConfirmationContextValue | null>(null);

export function useMicroConfirmation() {
  const context = useContext(MicroConfirmationContext);
  if (!context) {
    throw new Error(
      "useMicroConfirmation must be used within MicroConfirmationProvider",
    );
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

// Storage key for first-use tracking
const FIRST_USE_STORAGE_KEY = "nexus:microConfirmations:shown";

// Track if localStorage is available (private browsing, storage blocked, etc.)
let storageAvailable: boolean | null = null;

function isStorageAvailable(): boolean {
  if (storageAvailable !== null) return storageAvailable;
  try {
    const testKey = "__storage_test__";
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    storageAvailable = true;
  } catch {
    storageAvailable = false;
  }
  return storageAvailable;
}

function getShownKeys(): Set<string> {
  if (!isStorageAvailable()) return new Set();
  try {
    const stored = localStorage.getItem(FIRST_USE_STORAGE_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function saveShownKey(key: string): void {
  if (!isStorageAvailable()) return;
  try {
    const shown = getShownKeys();
    shown.add(key);
    localStorage.setItem(FIRST_USE_STORAGE_KEY, JSON.stringify([...shown]));
  } catch {
    // Ignore storage errors
  }
}

export function MicroConfirmationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [confirmations, setConfirmations] = useState<MicroConfirmationState[]>(
    [],
  );
  const [shownKeys, setShownKeys] = useState<Set<string>>(() => getShownKeys());

  const hasShown = useCallback(
    (key: string) => shownKeys.has(key),
    [shownKeys],
  );

  const markAsShown = useCallback((key: string) => {
    saveShownKey(key);
    setShownKeys((prev) => new Set([...prev, key]));
  }, []);

  const show = useCallback(
    (config: MicroConfirmationConfig) => {
      // Check first-use tracking
      if (config.firstUseKey) {
        // Write to storage BEFORE checking in-memory state to prevent cross-tab races
        // Check localStorage directly for most current state
        const persistedKeys = getShownKeys();
        if (
          persistedKeys.has(config.firstUseKey) ||
          shownKeys.has(config.firstUseKey)
        ) {
          // Already shown this first-use confirmation, skip
          return;
        }
        // Mark as shown BEFORE animating in (prevents races)
        saveShownKey(config.firstUseKey);
        setShownKeys((prev) => new Set([...prev, config.firstUseKey!]));
      }

      const id = `mc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const newConfirmation: MicroConfirmationState = {
        ...config,
        id,
        visible: true,
        duration: config.duration ?? 4000,
        variant: config.variant ?? "success",
      };

      // Queue management: max 2 visible at once
      setConfirmations((prev) => {
        const visible = prev.filter((c) => c.visible);
        if (visible.length >= 2) {
          // Dismiss oldest to make room
          const oldest = visible[0];
          setTimeout(() => {
            setConfirmations((p) =>
              p.map((c) => (c.id === oldest.id ? { ...c, visible: false } : c)),
            );
          }, 0);
        }
        return [...prev, newConfirmation];
      });

      // Auto-dismiss
      setTimeout(() => {
        setConfirmations((prev) =>
          prev.map((c) => (c.id === id ? { ...c, visible: false } : c)),
        );
        // Remove from DOM after animation
        setTimeout(() => {
          setConfirmations((prev) => prev.filter((c) => c.id !== id));
        }, 300);
      }, newConfirmation.duration);
    },
    [shownKeys],
  );

  const dismiss = useCallback((id: string) => {
    setConfirmations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, visible: false } : c)),
    );
    setTimeout(() => {
      setConfirmations((prev) => prev.filter((c) => c.id !== id));
    }, 300);
  }, []);

  return (
    <MicroConfirmationContext.Provider value={{ show, hasShown, markAsShown }}>
      {children}
      {/* Render confirmations - aria-live="polite" for screen reader announcement */}
      <div
        className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
        role="status"
        aria-live="polite"
        aria-atomic="false"
      >
        {confirmations.map((confirmation) => (
          <MicroConfirmationToast
            key={confirmation.id}
            {...confirmation}
            onDismiss={() => dismiss(confirmation.id)}
          />
        ))}
      </div>
    </MicroConfirmationContext.Provider>
  );
}

// ============================================================================
// Toast Component
// ============================================================================

interface MicroConfirmationToastProps extends MicroConfirmationState {
  onDismiss: () => void;
}

function MicroConfirmationToast({
  message,
  icon,
  variant = "success",
  visible,
  onDismiss,
}: MicroConfirmationToastProps) {
  const variantStyles = {
    success: {
      bg: "bg-[var(--ws-status-success-subtle,#ecfdf5)]",
      border: "border-[var(--ws-status-success,#10b981)]/20",
      icon: "text-[var(--ws-status-success,#10b981)]",
    },
    info: {
      bg: "bg-[var(--ws-surface-elevated)]",
      border: "border-[var(--ws-border-soft)]",
      icon: "text-[var(--ws-accent)]",
    },
    neutral: {
      bg: "bg-[var(--ws-surface-elevated)]",
      border: "border-[var(--ws-border-soft)]",
      icon: "text-[var(--ws-text-secondary)]",
    },
  };

  const styles = variantStyles[variant];

  return (
    <div
      className={`
        pointer-events-auto
        flex items-start gap-3 px-4 py-3
        rounded-lg border shadow-lg
        ${styles.bg} ${styles.border}
        transition-all duration-300 ease-out
        ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}
        max-w-sm
      `}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 ${styles.icon}`}>
        {icon || <Check className="h-4 w-4" />}
      </div>

      {/* Message */}
      <p className="text-sm text-[var(--ws-text)] leading-snug flex-1">
        {message}
      </p>

      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        className="flex-shrink-0 text-[var(--ws-text-tertiary)] hover:text-[var(--ws-text-secondary)] transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ============================================================================
// Convenience hook for common confirmations
// ============================================================================

/**
 * Pre-built confirmation messages for common actions.
 * These explain WHY the action mattered, building mental models.
 */
export const MICRO_CONFIRMATIONS = {
  noteSaved: {
    message:
      "Saved. This note now appears in the donor's timeline and is searchable.",
  },
  interactionLogged: {
    message:
      "Logged. This interaction contributes to engagement scoring and AI recommendations.",
  },
  giftRecorded: {
    message:
      "Recorded. This gift updates the donor's lifetime value and giving patterns.",
  },
  taskCreated: {
    message:
      "Created. This task will appear in your task list and can be assigned to team members.",
  },
  campaignCreated: {
    message:
      "Created. This campaign is now trackable across donors, gifts, and performance metrics.",
  },
  segmentSaved: {
    message:
      "Saved. This segment can now be used for targeting in campaigns and exports.",
  },
  filterSaved: {
    message: "Saved. You can reuse this filter view from the saved views menu.",
  },
  emailSent: {
    message:
      "Sent. This email is logged to the donor's timeline and included in engagement metrics.",
  },
  donorUpdated: {
    message:
      "Updated. Changes are reflected across all views and reports immediately.",
  },
} as const;

export default MicroConfirmationProvider;
